import React from "react";
import styled from "styled-components";
import { Colors } from "../../config";
import Button from "./Button";
import Typography from "./Typography"; // Keep Typography as is
import IconButton from "./IconButton";
import { useBizTripBookingContext } from "../../contexts/BizTripBookingContext";
import dayjs from "dayjs";
import { useState } from "react";
import Modal from "../Modal";

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
`;

const Header = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  align-items: center;
`;

const RightDetailsContainer = styled.div`
  width: 65%;
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;

const ConfirmContainer = styled.div`
  display: flex;
  gap: 2rem;
  @media only screen and (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const Card = styled.div`
  width: 35%;
  background-color: ${Colors.brandOne};
  border-radius: 1rem;
  box-sizing: border-box;
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;

const CardImage = styled.img`
  width: 100%;
`;

const CardContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FlightDetails = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

const FlightInfoColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TextLarge = styled.div`
  font-size: 32px;
  font-weight: 500;
  color: ${Colors.white};
`;

const TextSmall = styled.div`
  font-size: 13px;
  color: ${Colors.brandTwo};
`;

const TextGray = styled.div`
  color: ${Colors.lightGray};
`;

const DetailsSection = styled.div`
  width: 100%;
  background: ${Colors.white};
  padding: 1.5rem;
  box-sizing: border-box;
  border-radius: 1rem;
`;

const SectionTitle = styled.div`
  font-size: 1.1rem;
  color: ${Colors.brandTwo};
  font-weight: 600;
  margin-bottom: 8px;
  margin-top: 8px;
  @media only screen and (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const SectionTitleMain = styled.div`
  font-size: 1.5rem;
  color: ${Colors.brandTwo};
  font-weight: 600;
  margin-bottom: 8px;
  padding-top: 16px;
  border-top: 1px dashed ${Colors.alto};
  @media only screen and (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-size: 0.9rem;
  @media only screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoColumn = styled.div`
  width: ${(props) => props.span ?? "25%"};
  padding: 12px 0;
  box-sizing: border-box;
  text-align: left;
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  gap: 0.5rem;
  @media only screen and (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledBox = styled.div`
  padding: 1.5rem 1rem;
  display: flex;
  gap: 2rem;
  max-width: 350px;
  text-align: center;
  flex-direction: column;
  @media only screen and (max-width: 768px) {
    width: 300px;
  }
`;

const ConfirmBooking = ({
  onBackClick,
  onSubmit,
  leaveDetails,
  privilegeCard,
  isBusinessTrip = false,
  bookingComments = "NA",
}) => {
  const [showConfirmModal, setConfirmModal] = useState(false);
  const [nonSelectedBr, setNonSelectedBr] = useState([]);
  const { bizData, userData, activeTripType } = useBizTripBookingContext();

  const handleSubmit = () => {
    let nonSelectedBr = bizData
      .map((trip, index) => {
        const unselected = trip.businessRequirement
          .filter((br) => !br.isChecked)
          .map((br) => br.name);

        return {
          tripIndex: index + 1,
          names: unselected,
        };
      })
      .filter((trip) => trip.names.length > 0);

    setNonSelectedBr(nonSelectedBr);

    if (nonSelectedBr?.length > 0) {
      setConfirmModal(true);
    } else {
      onSubmit();
    }
  };

  return (
    <Container>
      <Header>
        <IconButton
          onClick={onBackClick}
          icon={<img src={require("../../assets/images/Arrow_Left.png")} />}
        />
        <Typography fontSize={"20px"} fontColor={Colors.white}>
          Confirm Request
        </Typography>
      </Header>

      <ConfirmContainer>
        <Card>
          <CardImage
            src={require("../../assets/images/image_flighttravel.png")}
          />
          <CardContent>
            <div>
              <TextSmall>Business Trip Passenger</TextSmall>
              <Typography fontColor={Colors.white}>
                {userData?.formalName}
              </Typography>
            </div>
            {bizData.map((tripData, idx) => (
              <div style={{ marginBottom: "2rem" }}>
                <FlightDetails>
                  <FlightInfoColumn>
                    <Typography fontColor={Colors.white}>
                      {tripData.type}
                    </Typography>
                  </FlightInfoColumn>
                  <FlightInfoColumn>
                    <Typography fontColor={Colors.white}>
                      {tripData.flightType}
                    </Typography>
                  </FlightInfoColumn>
                </FlightDetails>

                <FlightDetails>
                  <FlightInfoColumn>
                    <TextSmall>From</TextSmall>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                      }}
                    >
                      <TextLarge>{tripData.from}</TextLarge>
                      <img
                        style={{ width: "1.75rem" }}
                        src={require("../../assets/images/biz_onboard.png")}
                      />
                    </div>
                  </FlightInfoColumn>

                  <FlightInfoColumn>
                    <TextSmall>To</TextSmall>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                      }}
                    >
                      <TextLarge>{tripData.to}</TextLarge>
                      <img
                        style={{ width: "1.75rem" }}
                        src={require("../../assets/images/biz_destination.png")}
                      />
                    </div>
                  </FlightInfoColumn>
                </FlightDetails>

                <FlightDetails>
                  <FlightInfoColumn>
                    <TextSmall>Departure Date & Time</TextSmall>
                    <TextLarge>
                      {dayjs(tripData.departureDate).format("MMM DD")}
                    </TextLarge>
                    <TextGray>
                      {dayjs(tripData.departureDate).format("dddd")}{" "}
                      {tripData.departureTime}
                    </TextGray>
                  </FlightInfoColumn>
                  <FlightInfoColumn>
                    <TextSmall>Return Date & Time</TextSmall>
                    <TextLarge>
                      {tripData.type !== "One Way" && tripData.returnDate
                        ? dayjs(tripData.returnDate).format("MMM DD")
                        : "-"}
                    </TextLarge>
                    {tripData.type !== "One Way" && tripData.returnDate && (
                      <TextGray>
                        {dayjs(tripData.returnDate).format("dddd")}{" "}
                        {tripData.returnTime}
                      </TextGray>
                    )}
                  </FlightInfoColumn>
                </FlightDetails>
              </div>
            ))}
          </CardContent>
        </Card>
        <RightDetailsContainer>
          <DetailsSection>
            <div style={{ color: Colors.red }}>
              <strong>Note:</strong> This business trip request includes a leave
              request component. Kindly review both the travel and leave
              details.
            </div>
            <SectionTitle>Leave Details</SectionTitle>
            <InfoRow>
              <InfoColumn>
                <Typography fontColor={Colors.gray} fontWeight={500}>
                  Start Date
                </Typography>
                <Typography fontWeight={500} color={Colors.black}>
                  {dayjs(leaveDetails?.startDate)?.format("DD MMM YYYY")}
                </Typography>
              </InfoColumn>
              <InfoColumn>
                <Typography fontColor={Colors.gray} fontWeight={500}>
                  Expected Return
                </Typography>
                <Typography fontWeight={500} color={Colors.black}>
                  {dayjs(leaveDetails?.endDate)?.format("DD MMM YYYY")}
                </Typography>
              </InfoColumn>
              <InfoColumn>
                <Typography fontColor={Colors.gray} fontWeight={500}>
                  Attachment
                </Typography>
                <Typography fontWeight={500} color={Colors.black}>
                  {leaveDetails?.attachmentUrl === "" ? (
                    "-"
                  ) : (
                    <a href={leaveDetails?.attachmentUrl}>Download</a>
                  )}
                </Typography>
              </InfoColumn>
              <InfoColumn>
                <Typography fontColor={Colors.gray} fontWeight={500}>
                  Purpose of Travel
                </Typography>
                <Typography fontWeight={500} color={Colors.black}>
                  {leaveDetails?.comments ? leaveDetails?.comments : "NA"}
                </Typography>
              </InfoColumn>
            </InfoRow>
            <SectionTitle>Traveller Details</SectionTitle>
            <Typography
              fontSize="1.25rem"
              fontColor={Colors.prussianBlue}
              fontWeight={600}
            >
              {userData?.formalName}
            </Typography>

            <InfoRow>
              <InfoColumn>
                <Typography fontColor={Colors.gray} fontWeight={500}>
                  Employee ID
                </Typography>
                <Typography fontWeight={500} color={Colors.black}>
                  {userData?.empId}
                </Typography>
              </InfoColumn>
              <InfoColumn>
                <Typography fontColor={Colors.gray} fontWeight={500}>
                  Contact Number
                </Typography>
                <Typography fontWeight={500} color={Colors.black}>
                  {userData?.phoneNumber ?? "-"}
                </Typography>
              </InfoColumn>
              <InfoColumn>
                <Typography fontColor={Colors.gray} fontWeight={500}>
                  Passport Number
                </Typography>
                <Typography fontWeight={500} color={Colors.black}>
                  {userData?.passportNumber ?? "-"}
                </Typography>
              </InfoColumn>
              <InfoColumn>
                <Typography fontColor={Colors.gray} fontWeight={500}>
                  Passport Expiry
                </Typography>
                <Typography fontWeight={500} color={Colors.black}>
                  {dayjs(userData?.passportExpirationDate).format(
                    "DD MMM YYYY",
                  ) ?? "-"}
                </Typography>
              </InfoColumn>
              {isBusinessTrip && (
                <InfoColumn>
                  <Typography fontColor={Colors.gray} fontWeight={500}>
                    Privilege Club
                  </Typography>
                  <Typography fontWeight={500} color={Colors.black}>
                    {privilegeCard}
                  </Typography>
                </InfoColumn>
              )}
            </InfoRow>

            {bizData.map((tripData, idx) => (
              <>
                <SectionTitleMain>Trip-{idx + 1}</SectionTitleMain>
                <SectionTitle>Traveller Entitlements</SectionTitle>
                <InfoRow>
                  <InfoColumn>
                    <Typography fontColor={Colors.gray} fontWeight={500}>
                      Payment Type
                    </Typography>
                    <Typography fontWeight={500} color={Colors.black}>
                      {tripData?.paymentType}
                    </Typography>
                  </InfoColumn>
                  <InfoColumn>
                    <Typography fontColor={Colors.gray} fontWeight={500}>
                      Travel Class
                    </Typography>
                    <Typography fontWeight={500} color={Colors.black}>
                      {tripData?.travelClass}
                    </Typography>
                  </InfoColumn>
                  <InfoColumn>
                    <Typography fontColor={Colors.gray} fontWeight={500}>
                      Preferred Airlines
                    </Typography>
                    <Typography fontWeight={500} color={Colors.black}>
                      {tripData?.preferredAirlines
                        .filter((airline) => airline.selected === true)
                        .map((airline) => airline.label)
                        .join(",")}
                    </Typography>
                  </InfoColumn>
                  {isBusinessTrip && (
                    <>
                      <InfoColumn>
                        <Typography fontColor={Colors.gray} fontWeight={500}>
                          Flight Type
                        </Typography>
                        <Typography fontWeight={500} color={Colors.black}>
                          {tripData?.flightType}
                        </Typography>
                      </InfoColumn>
                      {idx === 0 && (
                        <>
                          <InfoColumn>
                            <Typography
                              fontColor={Colors.gray}
                              fontWeight={500}
                            >
                              Travel cost to be borne by Company
                            </Typography>
                            <Typography fontWeight={500} color={Colors.black}>
                              {tripData?.costBorneBy}
                            </Typography>
                          </InfoColumn>
                          <InfoColumn>
                            <Typography
                              fontColor={Colors.gray}
                              fontWeight={500}
                            >
                              Travel cost borne by Cost centre
                            </Typography>
                            <Typography fontWeight={500} color={Colors.black}>
                              {tripData?.costCenter}
                            </Typography>
                          </InfoColumn>
                          <InfoColumn>
                            <Typography
                              fontColor={Colors.gray}
                              fontWeight={500}
                            >
                              Project ID
                            </Typography>
                            <Typography fontWeight={500} color={Colors.black}>
                              {tripData?.projectId ? tripData?.projectId : "NA"}
                            </Typography>
                          </InfoColumn>
                        </>
                      )}
                    </>
                  )}
                </InfoRow>

                <SectionTitle>Selected Services</SectionTitle>
                <InfoRow>
                  {tripData?.businessRequirement.map((item) => (
                    <InfoColumn>
                      <Typography fontColor={Colors.gray} fontWeight={500}>
                        {item.name}
                      </Typography>
                      <Typography fontWeight={500} color={Colors.black}>
                        {item.isChecked
                          ? item?.hasMultipleSelection ? item?.selected.join(', '): item?.selected || item.amount || "Yes"
                          : "No"}
                      </Typography>
                    </InfoColumn>
                  ))}
                </InfoRow>
              </>
            ))}
            <div style={{ borderTop: `1px dashed ${Colors.alto}` }}></div>
            <SectionTitle>Special Request</SectionTitle>
            {bookingComments}
          </DetailsSection>
          <ButtonGroup>
            <Button
              label="Cancel Request"
              isRounded
              isOutlined
              color={Colors.white}
              width="250px"
              onClick={onBackClick}
            />
            <Button
              label="Confirm Request"
              isRounded
              color={Colors.brandTwo}
              width="250px"
              onClick={handleSubmit}
            />
          </ButtonGroup>
        </RightDetailsContainer>
      </ConfirmContainer>
      {showConfirmModal && (
        <Modal setClose={() => setConfirmModal(false)}>
          <StyledBox>
            <div
              style={{
                fontSize: "1.1rem",
                color: Colors.brandTwo,
                fontWeight: "bold",
              }}
            >
              Proceed Without Optional Services?
            </div>
            <div
              style={{
                color: Colors.brandOne,
                display: "flex",
                gap: "0.5rem",
                flexDirection: "column",
              }}
            >
              <div>You haven’t selected</div>
              <div
                style={{
                  color: Colors.brandOne,
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.25rem",
                  textAlign: "left",
                }}
              >
                {nonSelectedBr?.map((trip, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      textAlign: "center",
                      flexDirection: "column",
                      marginTop: "0.5rem",
                    }}
                  >
                    <strong>Trip {trip?.tripIndex}</strong>{" "}
                    <div>{trip?.names?.join(", ")}</div>
                  </div>
                ))}
              </div>
              <div>Continue booking?</div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Button
                onClick={() => setConfirmModal(false)}
                label={"Cancel"}
                color={Colors.brandOne}
                isOutlined={true}
                width="50%"
                size="sm"
              />
              <Button
                onClick={() => {
                  onSubmit();
                  setConfirmModal(false);
                }}
                label={"Confirm"}
                color={Colors.brandOne}
                width="50%"
                size="sm"
              />
            </div>
          </StyledBox>
        </Modal>
      )}
    </Container>
  );
};

export default ConfirmBooking;
